import { Button } from 'antd';
import React, { useState } from 'react';
import Icon, { ICON_NAME } from '../../../../components/Icon';
import Menu from '../../../../components/Menu';
import Footer from '../../../../components/Popup/_components/Footer';
import modal from '../../../../components/Popup/ModalCustom';
import { VesselService } from '../../../../util/services/vessel';
import { selectPermissions, selectTenantId } from '../../../../util/store/selectors';
import { useSelector } from '../../../../util/store/store';
import { showErrorResponse } from '../../../../util/varibles/global';
import { IVessel } from '../../../../util/varibles/interface';
import { ACTION } from '../../../../util/varibles/permission';
import CostInformation, { ICostInformationValue, validateCostInformation } from '../../../VesselManagementPage/PopupEditVessel/_component/CostInformation';

interface IProps {
    vessel: IVessel
    onSave: (vessel: IVessel) => void
}

const CostInfo: React.FC<IProps> = ({ vessel, onSave }) => {
    const [canUpdateCostInfo] = useSelector(selectPermissions([ACTION.VESSEL.COST_INFO_UPDATE]));
    const tenantId = useSelector(selectTenantId);
    const isVesselOwner = `${tenantId}` === `${vessel.vessel_owner_id || vessel.tenant_id}`;
    const { contracts = [] } = vessel;
    const hasContract = contracts.some(contract => `${contract.tenant_id}` === `${tenantId}`);
    const canConfigure = canUpdateCostInfo && (isVesselOwner || hasContract || contracts.length === 0);
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState<ICostInformationValue>({});
    const [loading, setLoading] = useState(false);
    const hasCostInfo = vessel.hourly_cost != null || vessel.cleaning_agent != null || Boolean(vessel.start_point?.id);
    const isEditing = editing && canConfigure;
    const transitionClassName = 'grid transition-[grid-template-rows,opacity,visibility] duration-300 ease-in-out motion-reduce:transition-none';

    const handleConfigure = () => {
        if (!canConfigure || loading) return;
        setValue({
            hourly_cost: vessel.hourly_cost,
            cleaning_agent: vessel.cleaning_agent,
            start_point: vessel.start_point,
        });
        setEditing(true);
    };

    const handleSave = async () => {
        if (!canConfigure || loading || Object.keys(validateCostInformation(value)).length > 0) return;
        setLoading(true);
        try {
            await VesselService.billingConfig({ ...value, vessel_id: vessel.id });
            onSave({ ...vessel, ...value });
            setEditing(false);
        } catch (error) {
            showErrorResponse('Update cost info failed', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = () => {
        if (!canConfigure || loading) return;
        modal.confirm({
            content: 'Are you sure you want to delete this cost info?',
            ok: {
                danger: true,
                click: async () => {
                    const cleared: ICostInformationValue = {
                        hourly_cost: null,
                        cleaning_agent: null,
                        start_point: {},
                    };
                    setLoading(true);
                    try {
                        await VesselService.billingConfig({ ...cleared, vessel_id: vessel.id });
                        onSave({ ...vessel, ...cleared });
                        setValue(cleared);
                    } catch (error) {
                        showErrorResponse('Delete cost info failed', error);
                    } finally {
                        setLoading(false);
                    }
                },
            },
        });
    };

    console.log(hasCostInfo, canConfigure);

    return <div className="mt-4 mb-4">
        <div className="flex items-center gap-3 mb-1 h-[30px]">
            <div className="text-[15px] font-[600] mt-1">Cost info.</div>
            {hasCostInfo && canConfigure && !isEditing && <Menu
                disabled={loading}
                items={[{
                    key: 'configure',
                    label: <div className="menu-line">
                        <Icon icon={ICON_NAME.EDIT} size={15} />
                        <span>Update cost info</span>
                    </div>,
                    onClick: handleConfigure,
                }, {
                    key: 'delete',
                    danger: true,
                    label: <div className="menu-line" data-danger={true}>
                        <Icon icon={ICON_NAME.DELETE} size={15} danger />
                        <span>Delete cost info</span>
                    </div>,
                    onClick: handleDelete,
                }]}
            />}
        </div>
        {canConfigure && <div
            className={`${transitionClassName} ${isEditing ? 'visible grid-rows-[1fr] opacity-100' : 'invisible grid-rows-[0fr] opacity-0'}`}
            aria-hidden={!isEditing}
        >
            <div className="min-h-0 overflow-hidden">
                <div className="pt-3">
                    <CostInformation value={value} onChange={setValue} disabled={loading || !isEditing} />
                    <Footer
                        className="mt-2 border-t border-[var(--border3)] pt-4"
                        cancel={{ click: () => { if (!loading) setEditing(false); } }}
                        ok={{ click: handleSave, loading, disabled: !isEditing }}
                    />
                </div>
            </div>
        </div>}
        <div
            className={`${transitionClassName} ${isEditing ? 'invisible grid-rows-[0fr] opacity-0' : 'visible grid-rows-[1fr] opacity-100'}`}
            aria-hidden={!!isEditing}
        >
            <div className="min-h-0 overflow-hidden">
                {hasCostInfo ? <div className="space-y-1 text-[15px]">
                    <div><span className="text-[var(--colorSub)]">Hourly cost: </span>{vessel.hourly_cost ?? '—'} kr/h</div>
                    <div><span className="text-[var(--colorSub)]">Cleaning agent cost: </span>{vessel.cleaning_agent ?? '—'} kr</div>
                    <div><span className="text-[var(--colorSub)]">Default start location: </span>{vessel.start_point?.name || '—'}</div>
                    <div className="italic text-[var(--colorSub)]">
                        (Ocean Planner uses this location as the starting point to calculate the traveling time to the operation location.)
                    </div>
                </div> : <div className="w-fit max-w-full">
                    <div className="text-comment italic">
                        Configure prices and the default starting location here to calculate operation costs.
                    </div>
                    {canConfigure && <div className="mt-1 flex justify-center">
                        <Button type="link" className="!h-auto !p-0" onClick={handleConfigure} tabIndex={isEditing ? -1 : 0}>
                            <Icon icon={ICON_NAME.EDIT} bg="colorLink" />
                            <span className="underline">Configure cost info</span>
                        </Button>
                    </div>}
                </div>}
            </div>
        </div>
    </div>;
};

export default CostInfo;
